import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import roleRequests from "../../api/RoleRequests";
import Loader from "../../components/utils/Loader";
import Navbar from "../../components/dashboard/Navbar";
import {
  Users,
  Plus,
  Search,
  Edit3,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  Shield,
  AlertTriangle,
  Loader2,
  XCircle,
} from "lucide-react";

const Roles = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  // Fetch roles
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await roleRequests.getRoles();

      if (response.data.status_code === "000") {
        const rolesData = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setRoles(rolesData);
      } else {
        toast.error(response.data.reason || "Failed to fetch roles");
        setRoles([]);
      }
    } catch (error) {
      toast.error("Failed to fetch roles");
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const openPermissionsModal = (role) => {
    setSelectedRole(role);
    setShowPermissionsModal(true);
  };

  const handleEditRole = (role) => {
    navigate(`/roles/edit/${role.role_slug}`);
  };

  const closePermissionsModal = () => {
    setShowPermissionsModal(false);
    setSelectedRole(null);
  };

  // Delete role handlers
  const openDeleteModal = (role) => {
    setRoleToDelete(role);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setRoleToDelete(null);
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;

    setDeleteLoading(true);
    try {
      const response = await roleRequests.deleteRole(roleToDelete.role_slug);

      if (response.success) {
        toast.success("Role deleted successfully!");
        closeDeleteModal();
        fetchRoles(); // Refresh the roles list
      } else {
        toast.error(response.message || "Failed to delete role");
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error("Failed to delete role");
    } finally {
      setDeleteLoading(false);
    }
  };

  const rolesArray = Array.isArray(roles) ? roles : [];
  let filteredRoles = [];
  try {
    filteredRoles = rolesArray.filter((role) =>
      role?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  } catch (error) {
    console.error("Error filtering roles:", error);
    filteredRoles = [];
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const tabVariants = {
    hidden: {
      opacity: 0,
      x: 20,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      x: -20,
      scale: 0.95,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  };

  if (loading && roles.length === 0) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
          <Loader />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
        <div className="px-4 py-6 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">User Roles</h1>
                <p className="mt-2 text-gray-600">
                  Manage user roles and their permissions
                </p>
              </div>
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/roles/add")}
                  className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  Add Role
                </motion.button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 gap-6 mb-8 sm:grid-cols-2 lg:grid-cols-3"
          >
            <motion.div
              variants={itemVariants}
              className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Roles
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {(roles || []).length}
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-xl">
                  <Users className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Active Roles
                  </p>
                  <p className="text-3xl font-bold text-red-600">
                    {Array.isArray(roles)
                      ? roles.filter(
                          (role) =>
                            role?.status === "active" || !role?.status
                        ).length
                      : 0}
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Inactive Roles
                  </p>
                  <p className="text-3xl font-bold text-orange-600">
                    {Array.isArray(roles)
                      ? roles.length -
                        roles.filter(
                          (role) =>
                            role?.status === "active" || !role?.status
                        ).length
                      : 0}
                  </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-xl">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Roles Section */}
          <motion.div
            variants={tabVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="mx-auto w-full max-w-full md:max-w-8xl"
          >
            <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-xl sm:rounded-3xl">
              {/* Header */}
              <div className="p-6 border-b border-gray-100 sm:p-8">
                <div className="flex gap-3 items-center sm:gap-4">
                  <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-xl sm:w-12 sm:h-12">
                    <Users className="w-5 h-5 text-blue-600 sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
                      User Roles
                    </h2>
                    <p className="text-sm text-gray-600">
                      Manage user roles and their associated permissions
                    </p>
                  </div>
                </div>
              </div>

              {/* Search and Filters */}
              <div className="p-4 border-b border-gray-100 sm:p-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-1">
                    {/* Search */}
                    <div className="relative flex-1 max-w-md">
                      <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search roles..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Results Count */}
                  <div className="text-sm text-gray-600">
                    {filteredRoles.length} of {(roles || []).length} roles
                  </div>
                </div>
              </div>

              {/* Roles List */}
              <div className="p-4 sm:p-8">
                {filteredRoles.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredRoles.map((role, index) => {
                      const isActive =
                        role.status === "active" || !role.status;
                      return (
                        <motion.div
                          key={role.role_slug || index}
                          variants={itemVariants}
                          className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-300"
                        >
                          {/* Card Header */}
                          <div className="flex-shrink-0 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                            <div className="flex justify-between items-start">
                              <div className="flex flex-1 gap-3 items-center min-w-0">
                                <div className="flex-shrink-0 p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-sm">
                                  <Users className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-gray-900 truncate">
                                    {role.name}
                                  </h3>
                                  <p className="text-xs font-medium text-blue-600">
                                    ID: {role.role_slug}
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-shrink-0 gap-1">
                                <button
                                  onClick={() => handleEditRole(role)}
                                  className="p-1.5 text-gray-500 rounded-md hover:text-blue-600 hover:bg-blue-100 transition-colors"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => openDeleteModal(role)}
                                  className="p-1.5 text-gray-500 rounded-md hover:text-red-600 hover:bg-red-100 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Card Body */}
                          <div className="flex flex-1 flex-col min-h-0 p-4">
                            <div className="flex-1 min-h-0">
                              {/* Permissions Summary */}
                              <div className="mb-3">
                                <div className="flex justify-between items-center mb-2">
                                  <h4 className="text-sm font-medium text-gray-700">
                                    Permissions
                                  </h4>
                                  <span className="text-xs text-gray-500">
                                    {role.permissions?.length || 0} total
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {role.permissions
                                    ?.slice(0, 3)
                                    .map((permission, permIndex) => (
                                      <span
                                        key={
                                          permission.permission_slug || permIndex
                                        }
                                        className="px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-md border border-purple-200"
                                      >
                                        {permission.name}
                                      </span>
                                    ))}
                                  {role.permissions?.length > 3 && (
                                    <button
                                      onClick={() => openPermissionsModal(role)}
                                      className="px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-md border border-indigo-200 transition-colors cursor-pointer hover:bg-indigo-200 hover:border-indigo-300"
                                    >
                                      +{role.permissions.length - 3} more
                                    </button>
                                  )}
                                  {(!role.permissions ||
                                    role.permissions.length === 0) && (
                                    <span className="px-2 py-1 text-xs text-orange-600 bg-orange-100 rounded-md border border-orange-200">
                                      No permissions
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Card Footer — fixed at bottom */}
                            <div className="mt-auto flex-shrink-0 pt-3 border-t border-gray-100">
                              <div className="flex justify-between items-center text-xs text-gray-500">
                                <span>
                                  Created:{" "}
                                  {new Date(role.created_at).toLocaleDateString()}
                                </span>
                                <span
                                  className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full shadow-sm ring-1 ring-black/5 ${
                                    isActive
                                      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                      : "bg-amber-50 text-amber-700 ring-amber-200"
                                  }`}
                                >
                                  {isActive ? (
                                    <>
                                      <CheckCircle className="w-3.5 h-3.5" />
                                      Active
                                    </>
                                  ) : (
                                    <>
                                      <XCircle className="w-3.5 h-3.5" />
                                      Inactive
                                    </>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <AlertCircle className="mx-auto w-12 h-12 text-gray-400" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">
                      No roles found
                    </h3>
                    <p className="mt-2 text-gray-500">
                      {searchTerm
                        ? "Try adjusting your search criteria."
                        : "No roles have been created yet."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Permissions Modal */}
      <AnimatePresence>
        {showPermissionsModal && selectedRole && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={closePermissionsModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                <div className="flex gap-3 items-center">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-sm">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Permissions for {selectedRole.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {selectedRole.permissions?.length || 0} total permissions
                    </p>
                  </div>
                </div>
                <button
                  onClick={closePermissionsModal}
                  className="p-2 text-gray-500 bg-white rounded-lg border border-gray-200 shadow-sm transition-all duration-200 hover:text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="overflow-y-auto flex-1 p-6">
                {selectedRole.permissions &&
                selectedRole.permissions.length > 0 ? (
                  <div className="space-y-4">
                    {selectedRole.permissions.map((permission, index) => (
                      <motion.div
                        key={
                          permission.permission_slug || permission.id || index
                        }
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 transition-colors hover:border-purple-300"
                      >
                        <div className="flex items-start">
                          <div className="flex-1">
                            <div className="flex gap-3 items-center mb-2">
                              <div className="p-1.5 bg-purple-100 rounded-md">
                                <Shield className="w-4 h-4 text-purple-600" />
                              </div>
                              <h4 className="font-medium text-gray-900">
                                {permission.name ||
                                  permission.permission_name ||
                                  permission.display_name ||
                                  "Unnamed Permission"}
                              </h4>
                            </div>
                            <div className="ml-8 space-y-1">
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Module:</span>{" "}
                                {permission.module ||
                                  permission.module_name ||
                                  "General"}
                              </p>
                              {permission.description && (
                                <p className="text-sm text-gray-500">
                                  {permission.description}
                                </p>
                              )}
                              <p className="text-xs text-gray-400">
                                ID:{" "}
                                {permission.permission_slug ||
                                  permission.id ||
                                  permission.permission_id ||
                                  "N/A"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <div className="flex justify-center items-center p-4 mx-auto mb-4 w-16 h-16 bg-orange-100 rounded-full">
                      <AlertCircle className="w-8 h-8 text-orange-600" />
                    </div>
                    <h4 className="mb-2 text-lg font-medium text-gray-900">
                      No Permissions
                    </h4>
                    <p className="text-gray-600">
                      This role doesn't have any permissions assigned yet.
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex flex-shrink-0 justify-between items-center p-6 bg-gray-50 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Role ID:{" "}
                  <span className="font-mono text-blue-600">
                    {selectedRole.role_slug}
                  </span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={closePermissionsModal}
                    className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-all duration-200 hover:bg-gray-50 hover:border-gray-400 shadow-sm"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      closePermissionsModal();
                      navigate(`/roles/edit/${selectedRole.role_slug}`);
                    }}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg"
                  >
                    Edit Role
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && roleToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={closeDeleteModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="overflow-hidden relative w-full max-w-md bg-white rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex justify-center items-center p-8 bg-gradient-to-br from-red-50 to-orange-50">
                <div className="flex flex-col gap-4 items-center text-center">
                  <div className="flex justify-center items-center p-4 w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-full shadow-lg">
                    <AlertTriangle className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Delete Role
                    </h3>
                    <p className="mt-2 text-sm text-gray-600">
                      This action cannot be undone
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <div className="text-center">
                  <p className="mb-4 text-gray-700">
                    Are you sure you want to delete the role{" "}
                    <span className="font-semibold text-red-600">
                      "{roleToDelete.name}"
                    </span>
                    ?
                  </p>
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex gap-3 items-start">
                      <div className="flex-shrink-0 p-1.5 bg-red-100 rounded-md">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-red-800">
                          Warning
                        </p>
                        <p className="mt-1 text-xs text-red-600">
                          This will permanently remove the role and all its
                          associated permissions. Users with this role will lose
                          access to related features.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3 p-6 bg-gray-50 border-t border-gray-200">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={closeDeleteModal}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-all duration-200 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDeleteRole}
                  disabled={deleteLoading}
                  className="flex flex-1 gap-2 justify-center items-center px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-orange-500 rounded-lg shadow-lg transition-all duration-200 hover:from-red-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Role
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Roles;
